import { motion } from "framer-motion";
import { VALUES, STATS } from "../lib/data";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import NumberCounter from "../components/NumberCounter";
import { useSettings } from "../lib/useSettings";
import { usePageContent } from "../lib/usePageContent";
import { useStats } from "../lib/useStats";

const DEFAULT_HERO =
  "";

function renderAccent(text) {
  // Renders text with *word* converted into brass-color span
  if (!text) return null;
  const parts = String(text).split("*");
  return parts.map((p, i) =>
    i % 2 === 1 ? <span key={i} className="text-[#C48F56]">{p}</span> : <span key={i}>{p}</span>,
  );
}

export default function ChiSiamo() {
  const settings = useSettings();
  const c = usePageContent("chi-siamo", {});
  const stats = useStats();
  const heroImg = settings?.hero_about || DEFAULT_HERO;
  const modelLines = Array.isArray(c.model_lines) ? c.model_lines : [];
  return (
    <div data-testid="chi-siamo-page">
      <SEO
        title="Chi Siamo"
        path="/chi-siamo"
        description="Invest Hospitality: dal 2014 una holding italiana che unisce sviluppo immobiliare e gestione alberghiera a Milano. Scopri il nostro modello integrato."
        image={heroImg}
      />
      <PageHero
        eyebrow={settings?.hero_about_eyebrow || "Chi siamo"}
        title={
          settings?.hero_about_title ||
          "Dal progetto all'hotel:\nun modello *integrato.*"
        }
        subtitle={
          settings?.hero_about_subtitle ||
          "Invest Hospitality è una holding italiana specializzata nello sviluppo e nella gestione di hotel. Dal 2014 operiamo principalmente a Milano, seguendo ogni fase: dalla ricerca della location alla gestione diretta della struttura."
        }
        image={heroImg}
      />

      {/* Operating model */}
      <section className="relative py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
              <span className="h-px w-8 bg-[#C48F56]" />
              {c.model_eyebrow || "Il nostro modello"}
            </div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tightest leading-[0.95]">
              {renderAccent(c.model_title) || (
                <>Un approccio <span className="text-[#C48F56]">verticale.</span></>
              )}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-8 text-white/70 text-lg leading-relaxed">
            <p>{c.model_paragraph_1}</p>
            <div className="space-y-4">
              {modelLines.map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="flex gap-4 items-start"
                >
                  <span className="font-mono text-[11px] text-[#C48F56] pt-2">
                    0{i + 1}
                  </span>
                  <span>{line}</span>
                </motion.div>
              ))}
            </div>
            <p>{c.model_outro}</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto border-t border-white/5">
        <div className="mb-14">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
            <span className="h-px w-8 bg-[#C48F56]" />
            {c.values_eyebrow || "Cosa ci guida"}
          </div>
          <h2 className="mt-5 font-display text-5xl md:text-6xl lg:text-7xl tracking-tightest">
            {renderAccent(c.values_title) || (
              <>Quattro principi, <span className="text-[#C48F56]">uno standard.</span></>
            )}
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              className="rounded-2xl border border-white/10 bg-[#0F0F14] p-8 md:p-10"
            >
              <span className="font-mono text-[11px] text-white/30">
                0{i + 1}
              </span>
              <h3 className="mt-2 font-display text-3xl md:text-4xl tracking-tight">
                {v.title}
              </h3>
              <p className="mt-4 text-white/60 leading-relaxed">{v.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Milan focus */}
      <section className="relative py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto border-t border-white/5">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
              <span className="h-px w-8 bg-[#C48F56]" />
              {c.milan_eyebrow || "Perché Milano"}
            </div>
            <h2 className="mt-5 font-display text-5xl md:text-6xl tracking-tightest leading-[0.95]">
              {renderAccent(c.milan_title) || (
                <>Dinamica, internazionale,<br />in <span className="text-[#C48F56]">evoluzione.</span></>
              )}
            </h2>
          </div>
          <div className="lg:col-span-6 text-white/70 leading-relaxed text-lg space-y-5">
            <p>{c.milan_paragraph_1}</p>
            <p>{c.milan_paragraph_2}</p>
            <p>{c.milan_paragraph_3}</p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { label: c.stat_year_label || "Dal", value: STATS.foundedYear, animate: false },
            { label: c.stat_hotels_label || "Hotel attivi", value: stats?.hotels ?? STATS.hotels, animate: true },
            { label: c.stat_rooms_label || "Camere totali", value: stats?.rooms ?? STATS.rooms, animate: true },
            { label: c.stat_pipeline_label || "In pipeline", value: stats?.pipeline_rooms ?? STATS.pipelineRooms, animate: true, prefix: "+" },
          ].map((s) => (
            <div key={s.label} className="border-l border-white/10 pl-5">
              <div className="font-display text-5xl md:text-6xl tracking-tight">
                {s.animate ? (
                  <NumberCounter value={s.value} prefix={s.prefix || ""} />
                ) : (
                  s.value
                )}
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
